import { useCustomMutation } from '@refinedev/core';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Props {
  topupId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRedeemed: () => void;
}

export function RedeemDialog({ topupId, open, onOpenChange, onRedeemed }: Props) {
  const { mutate, isLoading } = useCustomMutation();
  const [accountId, setAccountId] = useState('');

  useEffect(() => {
    if (open) setAccountId('');
  }, [open]);

  const onSubmit = () => {
    if (!topupId) return;
    if (!UUID_RE.test(accountId.trim())) {
      toast.error('UUID da conta inválido.');
      return;
    }
    mutate(
      {
        url: `/topup/${topupId}/redeem`,
        method: 'post',
        values: { account_id: accountId.trim() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          toast.success('Top-up aplicado. Conta ficará Com saldo.');
          onOpenChange(false);
          onRedeemed();
        },
        onError: (err) => {
          const code = (err as unknown as { errorCode?: string })?.errorCode;
          if (code === 'topup_not_available') toast.error('Top-up não está disponível.');
          else if (code === 'account_not_found') toast.error('Conta não encontrada.');
          else toast.error('Falha ao aplicar.');
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Aplicar gift card em conta</DialogTitle>
          <DialogDescription>
            Cole o UUID da conta destino. Operação é atômica e gera audit log. Conta
            disponível ficará <span className="font-medium text-foreground">Com saldo</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="account_id">UUID da conta *</Label>
          <Input
            id="account_id"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Pegue o UUID na URL da página de detalhe da conta (`/contas/&lt;uuid&gt;`).
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={isLoading || !UUID_RE.test(accountId.trim())}>
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
